import React from 'react'
import { Link } from '@inertiajs/react'

// Light theme variants (admin/staff pages)
const lightVariants = {
  primary: 'text-white bg-sky-600 border-transparent hover:bg-sky-700 shadow-sm',
  secondary: 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50',
  danger: 'text-white bg-red-600 border-transparent hover:bg-red-700',
  ghost: 'text-gray-600 bg-transparent border-transparent hover:text-gray-900',
  muted: 'text-gray-600 bg-gray-100 border-transparent hover:bg-gray-200',
}

// Dark theme variants (customer-facing GuestOrder pages)
const darkVariants = {
  primary: 'text-white bg-sky-900 border-transparent hover:bg-sky-800 shadow-sm',
  secondary: 'text-sky-900 bg-white border-transparent hover:bg-gray-50',
  danger: 'text-white bg-red-600 border-transparent hover:bg-red-700',
  ghost: 'text-white bg-white/20 border-transparent hover:bg-white/30',
  muted: 'text-white/70 bg-white/10 border-transparent hover:bg-white/20',
}

const linkVariants = {
  primary: 'text-sky-600 hover:text-sky-500',
  secondary: 'text-gray-700 hover:text-gray-900',
  danger: 'text-red-600 hover:text-red-500',
  ghost: 'text-gray-600 hover:text-gray-900',
  muted: 'text-gray-600 hover:text-gray-900',
}

const lightSizes = {
  xs: 'px-2 py-1 text-xs',
  sm: 'px-3 py-1.5 text-sm',
  md: 'px-4 py-2 text-sm',
  lg: 'px-6 py-3 text-base',
}

const darkSizes = {
  xs: 'px-2 py-1 text-xs',
  sm: 'px-3 py-2 text-sm',
  md: 'px-4 py-3 text-base',
  lg: 'px-6 py-4 text-base',
}

export default function Button({
  children,
  variant = 'primary',
  theme = 'light',
  size = 'md',
  link = false,
  href,
  disabled = false,
  fullWidth = false,
  circular = false,
  className = '',
  type = 'button',
  ariaLabel,
  ...props
}) {
  const isDark = theme === 'dark'
  const variants = isDark ? darkVariants : lightVariants
  const sizes = isDark ? darkSizes : lightSizes

  // Base classes differ by theme
  const baseClasses = link
    ? 'inline-flex items-center font-medium transition focus:outline-none'
    : isDark
      ? `inline-flex items-center justify-center font-bold ${circular ? 'rounded-full' : 'rounded-lg'} border transition focus:outline-none focus:ring-2 focus:ring-offset-2 ${variant === 'ghost' ? 'focus:ring-white/50' : 'focus:ring-sky-500'} disabled:opacity-50 disabled:cursor-not-allowed`
      : `inline-flex items-center justify-center font-medium ${circular ? 'rounded-full' : 'rounded-md'} border transition focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500 disabled:opacity-50 disabled:cursor-not-allowed`

  const variantClasses = link
    ? (linkVariants[variant] || linkVariants.primary)
    : (variants[variant] || variants.primary)
  const sizeClasses = link ? 'text-sm' : (circular ? '' : (sizes[size] || sizes.md))
  const widthClasses = fullWidth ? 'w-full' : ''

  const classes = `${baseClasses} ${variantClasses} ${sizeClasses} ${widthClasses} ${className}`.trim()

  if (href) {
    return (
      <Link href={href} className={classes} {...props}>
        {children}
      </Link>
    )
  }

  return (
    <button type={type} disabled={disabled} className={classes} aria-label={ariaLabel} {...props}>
      {children}
    </button>
  )
}
